import { omit } from 'lodash-es';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import type { CurrencyType } from '@coin-depo-types';

import { StorageService } from '@coin-depo-core/storage';
import { CurrencyService } from '@coin-depo-core/currency.service';
import { BalancesFacade } from '@coin-depo-core/balances';

import { ApiService } from '@coin-depo/http';

import type {
  ApiResponse,
  BalanceDto,
  BalancesRequest,
  BalancesResponse,
  CreateDepositAccountRequest,
  IBonuses,
} from '@coin-depo-types';
import { AUTH_SERVICE_TOKEN, AuthFacade } from '@coin-depo-core/auth';

@Injectable()
export class BalancesService implements BalancesFacade {
  private balances$ = new BehaviorSubject<BalanceDto[]>(
    this.storage.get('balances', [])
  );
  cachedBalances$ = this.balances$.asObservable();

  private totalBalance$ = new BehaviorSubject<number>(
    this.storage.get('totalBalance', 0)
  );
  cachedTotalBalance$ = this.totalBalance$.asObservable();

  private availableCreditLimit$ = new BehaviorSubject<number>(
    this.storage.get('availableCreditLimit', 0)
  );
  cachedAvailableCreditLimit$ = this.availableCreditLimit$.asObservable();

  private creditLineLimit$ = new BehaviorSubject<number>(
    this.storage.get('creditLineLimit', 0)
  );
  cachedCreditLineLimit$ = this.creditLineLimit$.asObservable();

  private earnedReferrals$ = new BehaviorSubject<number>(
    this.storage.get('earnedReferrals', 0)
  );
  cachedEarnedReferrals$ = this.earnedReferrals$.asObservable();

  private bonusesPending$ = new BehaviorSubject<string>(
    this.storage.get('bonusesNew', 0)
  );
  cachedBonusesPending$ = this.bonusesPending$.asObservable()

  private bonusesPaid$ = new BehaviorSubject<string>(
    this.storage.get('bonusesPaid', 0)
  );

  cachedBonusesPaid$ = this.bonusesPaid$.asObservable();

  private tokenAdvantage$ = new BehaviorSubject<{
    balance: number;
    balance_currency: number;
    coindepo_percentage: string;
    tier_id: number;
  }>(
    this.storage.get('tokenAdvantage', null)
  );
  cachedTokenAdvantage$ = this.tokenAdvantage$.asObservable();

  constructor(
    @Inject(AUTH_SERVICE_TOKEN) private auth: AuthFacade,
    private balancesApi: ApiService,
    private storage: StorageService,
    private currencyService: CurrencyService
  ) {}

  updateBalances(
    currency?: CurrencyType,
    waiteUntil?: Observable<any>
  ): Observable<BalancesResponse | null> {
    const userData = this.auth.userData;
    const data: BalancesRequest = {
      parent_id: this.auth.parentId!,
      client_token: this.auth.accessToken,
      username: userData.email as string,
      user_id: userData.id!,
      currency: currency || this.currencyService.currentCurrency,
      cummulative: true,
    };

    return this.balancesApi.getBalances(data).pipe(
      map((res) => omit(res, ['parent_id', 'status', 'msg'])),
      tap((res: BalancesResponse) => {
        if (waiteUntil) {
          waiteUntil.subscribe(() => {
            this.setStateFromResponse(res);
          });
        } else {
          this.setStateFromResponse(res);
        }
      }),
      // Don't let a network failure (status 0) propagate to the global Sentry
      // ErrorHandler. Emit `null` so combineLatest in callers keeps firing,
      // matching the sibling stats/tokens streams. See remediation plan Fix C4.
      catchError(() => of(null))
    );
  }

  changeAccountVisibility(
    planId: string,
    walletName: string,
    visible = 1
  ): Observable<true | string> {
    const userData = this.auth.userData;

    const data: CreateDepositAccountRequest = {
      client_token: this.auth.accessToken,
      deposit_plan_id: parseInt(planId, 10),
      username: userData.email,
      visible,
      wallet_name: walletName,
    };

    return this.balancesApi.setAccountVisibility(data).pipe(
      map((res: ApiResponse) => (res.status === 'success' ? true : res.msg)),
      catchError((err: HttpErrorResponse) => of(err.error.msg))
    );
  }

  private setStateFromResponse(res: BalancesResponse) {
    this.setBalances(res.balances);
    this.setTotalBalance(res.total_balance);
    this.setAvailableCreditLimit(res.available_credit_limit);
    this.setCreditLineLimit(res.credit_line_limit);
    this.setEarnedReferrals(res.earned_referrals);
    this.setBonuses(res.bonuses_summary);
    this.setTokenAdvantage(res);
  }

  private setEarnedReferrals(earnedReferrals: number) {
    this.earnedReferrals$.next(earnedReferrals || 0);
    this.storage.set('earnedReferrals', earnedReferrals);
  }

  private setCreditLineLimit(creditLineLimit: number) {
    this.creditLineLimit$.next(creditLineLimit);
    this.storage.set('creditLineLimit', creditLineLimit);
  }

  private setAvailableCreditLimit(availableCreditLimit: number) {
    this.availableCreditLimit$.next(availableCreditLimit);
    this.storage.set('availableCreditLimit', availableCreditLimit);
  }

  private setTotalBalance(totalBalance: number) {
    this.totalBalance$.next(totalBalance);
    this.storage.set('totalBalance', totalBalance);
  }

  private setBalances(balances: BalanceDto[]) {
    if (balances.length) {
      this.balances$.next(balances);
      this.storage.set('balances', balances);
    }
  }

  private setBonuses(bonuses: IBonuses)  {
    this.bonusesPending$.next(bonuses.pending ?? 0)
    this.storage.set('bonusesPending', bonuses.pending ?? 0);

    this.bonusesPaid$.next(bonuses.paid ?? 0)
    this.storage.set('bonusesPaid', bonuses.paid ?? 0);
  }

  private setTokenAdvantage(res: BalancesResponse) {
    if (!res.balances || res.balances.length === 0 || !res.user_tier) return;

    const coindepoToken = res.balances.find(balance => balance.ticker === 'COINDEPO') as BalanceDto;
    const tokenObject = {
      balance: coindepoToken.balance,
      balance_currency: coindepoToken.balance_currency,
      coindepo_percentage: res.user_tier.coindepo_percentage ?? 0,
      tier_id: res.user_tier.tier_id ?? 1
    }
    
    this.tokenAdvantage$.next(tokenObject);
    this.storage.set('tokenAdvantage', tokenObject);
  }
}
